import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../../models/NameUtils';
import { copyToClipboard } from '../../../../../../models/UiUtils';
import { Button } from '../../../../../../web/components-v2/Button';
import { DataGridRow, DataGridRowItem } from '../../../../../../web/components/DataGrid';
import { ClickableSDRName } from '../../../../../components/ClickableSDRName';
import { useGetTranscriptionSpeakerLineViewQuery } from '../../../../../queries/Transcription/useGetTranscription';
import { CallAnalysisData } from '../../types';
import { styleSheet } from './styles';

interface CallAnalysisRowProps {
	call: CallAnalysisData;
	hasMultiSelectFeature?: boolean;
	isSelected?: boolean;
	onSelect?: (callId: string, selected: boolean, isBulk?: boolean) => void;
	onSDRClick?: (userId: string, userName: string) => void;
}

export const CallAnalysisRow: React.FC<CallAnalysisRowProps> = ({
	call,
	hasMultiSelectFeature = false,
	isSelected = false,
	onSelect,
	onSDRClick,
}) => {
	const { phoneCall, company, contact, creator, interaction } = call;

	// Only fetch transcription when needed (manual trigger or when selected)
	const [shouldFetchTranscription, setShouldFetchTranscription] = React.useState(false);

	// Enable transcription fetching when row is selected for multi-select operations
	React.useEffect(() => {
		if (isSelected && !shouldFetchTranscription) {
			setShouldFetchTranscription(true);
		}
	}, [isSelected, shouldFetchTranscription]);

	const transcriptionQuery = useGetTranscriptionSpeakerLineViewQuery({
		transcriptionId: phoneCall.transcriptionId || '',
		enabled: shouldFetchTranscription && !!phoneCall.transcriptionId,
	});

	const formatDuration = (milliseconds?: number) => {
		if (!milliseconds || milliseconds <= 0) return '--';

		// Convert milliseconds to seconds
		const totalSeconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const remainingSeconds = totalSeconds % 60;

		// Format as "Xm Ys"
		return `${minutes}m ${remainingSeconds}s`;
	};

	const formatCallTime = (dateString?: string) => {
		if (!dateString) return '--';
		try {
			return moment(dateString).format('M/DD/YYYY [at] h:mma');
		} catch {
			return '--';
		}
	};

	const getTranscriptionStatus = () => {
		if (!phoneCall.transcriptionId) {
			return { text: 'No Transcription', className: styleSheet.statusUnavailable };
		}

		// Check transcription status if available
		switch (phoneCall.recordingTranscriptionStatus) {
			case Api.PhoneCallRecordingTranscriptionStatus.AvailableForTranscription:
				return { text: 'Processing', className: styleSheet.statusProcessing };
			case Api.PhoneCallRecordingTranscriptionStatus.TranscriptionComplete:
				return { text: 'Available', className: styleSheet.statusAvailable };
			case Api.PhoneCallRecordingTranscriptionStatus.TranscriptionFailure:
				return { text: 'Failed', className: styleSheet.statusFailed };
			case Api.PhoneCallRecordingTranscriptionStatus.RecordingValidationFailure:
				return { text: 'Failed', className: styleSheet.statusFailed };
			default:
				return { text: 'Available', className: styleSheet.statusAvailable };
		}
	};

	const getSdrName = () => {
		if (!creator) return 'Unknown SDR';
		return getDisplayName(creator);
	};

	const getCallOutcome = () => {
		if (!interaction?.outcome) {
			return { name: 'No Outcome', color: '#666666' };
		}
		return {
			name: interaction.outcome.name || 'Unknown',
			color: interaction.outcome.hexColor || '#666666',
		};
	};

	const handleCopyTranscription = async () => {
		if (!phoneCall.transcriptionId) {
			toast.error('No transcription available to copy');
			return;
		}

		try {
			// Enable fetching if not already enabled
			if (!shouldFetchTranscription) {
				setShouldFetchTranscription(true);
			}

			// Always refetch to get the latest transcription data
			const result = await transcriptionQuery.refetch();

			if (!result.data) {
				toast.error('Failed to load transcription');
				return;
			}

			// Format the transcription text from speaker lines
			const transcriptionText =
				result.data.speakerLines?.map((line: any) => `${line.speaker}: ${line.line}`).join('\n') ||
				'No transcription content available';

			const success = await copyToClipboard(transcriptionText);

			if (success) {
				toast.success('Transcription copied to clipboard');
			} else {
				toast.error('Failed to copy transcription');
			}
		} catch (error) {
			console.error('Failed to copy transcription:', error);
			toast.error('Failed to copy transcription');
		}
	};

	const transcriptionStatus = getTranscriptionStatus();
	const contactName = contact ? getDisplayName(contact) : 'Unknown Contact';
	const companyName = company?.companyName || 'Unknown Company';
	const sdrName = getSdrName();
	const callOutcome = getCallOutcome();
	const isPracticeCall = phoneCall.leadCallMode === Api.LeadCallMode.PracticeCall;

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (onSelect && call.hasTranscription) {
			const isBulk = (event.nativeEvent as MouseEvent).shiftKey;
			onSelect(phoneCall.id, !isSelected, isBulk);
		}
	};

	return (
		<DataGridRow styles={[styleSheet.row]}>
			{hasMultiSelectFeature && (
				<DataGridRowItem styles={[styleSheet.cell]}>
					<input
						type='checkbox'
						checked={isSelected}
						onChange={handleCheckboxChange}
						disabled={!call.hasTranscription}
					/>
				</DataGridRowItem>
			)}
			<DataGridRowItem styles={[styleSheet.cell]}>
				<div className={css(styleSheet.contactInfo)}>
					<div className={css(styleSheet.contactName)}>
						{contactName}
						{isPracticeCall && (
							<span className={css(styleSheet.callTypeBadge, styleSheet.practiceCallBadge)}>Practice</span>
						)}
					</div>
					<div className={css(styleSheet.companyName)}>{companyName}</div>
				</div>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				<ClickableSDRName
					userId={creator?.id}
					userName={sdrName}
					className={css(styleSheet.sdrName)}
					onClick={onSDRClick}
				/>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				<div
					className={css(styleSheet.callOutcome)}
					style={{
						borderColor: callOutcome.color + '40',
						backgroundColor: callOutcome.color + '10',
					}}
				>
					{callOutcome.name}
				</div>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				<div className={css(styleSheet.callTime)}>{formatCallTime(phoneCall.startTime)}</div>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				<div className={css(styleSheet.duration)}>{formatDuration(phoneCall.duration)}</div>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				<div className={css(styleSheet.transcriptionStatus, transcriptionStatus.className)}>
					{transcriptionStatus.text}
				</div>
			</DataGridRowItem>

			<DataGridRowItem styles={[styleSheet.cell]}>
				{phoneCall.transcriptionId ? (
					<Button
						variant='secondary'
						size='sm'
						disabled={transcriptionQuery.isPending}
						onClick={handleCopyTranscription}
					>
						{transcriptionQuery.isPending ? 'Loading...' : 'Copy Transcript'}
					</Button>
				) : (
					<Button variant='secondary' size='sm' disabled>
						No Transcription
					</Button>
				)}
			</DataGridRowItem>
		</DataGridRow>
	);
};
